import { AIRPORTS, allCountries, countrySlug, findAirport } from "./airports";

// Top miasta wylotu
export const TOP_DEPARTURE_CITIES = [
  "WAW", "KRK", "GDN", "KTW", "WRO", "POZ", "WMI", "SZZ", "RZE", "LCJ", "BZG", "LUZ", "RDO",
];

export function departureCityLabel(code: string): string {
  const airport = findAirport(code.toUpperCase());
  if (!airport) return code;

  const departures = TOP_DEPARTURE_CITIES
    .map((departureCode) => findAirport(departureCode))
    .filter((a): a is NonNullable<ReturnType<typeof findAirport>> => !!a);
  const hasSameCityAirport = departures.some(
    (a) => a.code !== airport.code && a.city === airport.city && a.country === airport.country,
  );

  return hasSameCityAirport && !airport.isMetro && !airport.isCountry
    ? `${cityFromGen(airport.city)} ${airport.name}`
    : cityFromGen(airport.city);
}

// Top kierunki — łączone z TOP_DEPARTURE_CITIES → strony tras
// Rozszerzone do ~130 lotnisk: główne huby UK/FR/ES/IT/DE/CH/BE + Bałtyk +
// Bliski Wschód + Azja + Ameryki + Afryka, żeby pokryć więcej długiego ogona
// keywordów typu „tanie loty {miasto} → {kierunek}".
export const TOP_DESTINATIONS = [
  // UK / IE
  "LGW", "STN", "LTN", "LHR", "EDI", "MAN", "BHX", "BRS", "LPL", "DUB",
  // FR
  "CDG", "ORY", "NCE", "MRS", "LYS", "TLS",
  // ES (kontynent + wyspy)
  "BCN", "MAD", "PMI", "AGP", "ALC", "TFS", "LPA", "IBZ", "VLC", "SVQ", "BIO",
  // IT
  "FCO", "MXP", "NAP", "VCE", "CTA", "PMO", "BLQ", "PSA", "FLR", "BRI",
  // DE
  "FRA", "MUC", "BER", "DUS", "HAM", "STR", "CGN",
  // BeNeLux / CH / AT
  "AMS", "BRU", "CRL", "GVA", "ZRH", "VIE",
  // Skandynawia / Bałtyk / Islandia
  "CPH", "ARN", "OSL", "HEL", "RIX", "TLL", "VNO", "KEF",
  // Bałkany
  "PRG", "BUD", "SPU", "DBV", "ZAG", "PUY", "BEG", "TIA", "TIV", "SOF", "VAR", "BOJ",
  // TR / IL / Cypr / Malta
  "IST", "SAW", "ESB", "AYT", "TLV", "LCA", "MLA",
  // Grecja
  "ATH", "HER", "RHO", "JTR",
  // Afryka Północna
  "HRG", "SSH", "RMF", "RAK", "AGA",
  // Zatoka Perska
  "DXB", "AUH", "DOH",
  // Azja
  "BKK", "HKT", "DPS", "MLE", "SIN", "KUL", "HKG", "DEL", "BOM", "SGN", "HAN",
  "CMB", "NRT", "HND", "ICN", "PVG",
  // Ameryka Płn. + Karaiby
  "JFK", "MIA", "LAX", "ORD", "BOS", "SFO", "LAS", "DEN", "YYZ", "YUL", "YVR",
  "CUN", "PUJ", "MBJ", "HAV",
  // Ameryka Płd.
  "GIG", "GRU", "EZE",
  // Afryka Subsaharyjska
  "JNB", "CPT", "NBO", "ZNZ",
  // Pozostałe (Portugalia, Tunezja)
  "FAO", "LIS", "OPO", "TUN",
];

// Tematyczne landing pages
export const THEMES: { slug: string; label: string; description: string }[] = [
  { slug: "wakacyjne", label: "Tanie loty wakacyjne", description: "Najlepsze kierunki na urlop letni" },
  { slug: "city-break", label: "City break", description: "Krótkie wyjazdy do europejskich stolic" },
  { slug: "last-minute", label: "Last minute", description: "Oferty w ostatniej chwili" },
  { slug: "egzotyczne", label: "Egzotyczne", description: "Daleka podróż za rozsądną cenę" },
  { slug: "narty", label: "Narty", description: "Loty w góry na sezon zimowy" },
  { slug: "morze", label: "Nad morze", description: "Letnie kurorty nadmorskie" },
  { slug: "swiateczne", label: "Świąteczne", description: "Loty na święta i sylwestra" },
  { slug: "majowka", label: "Majówka", description: "Długi weekend majowy" },
  { slug: "weekend", label: "Weekendowe", description: "Wyjazdy piątek-niedziela" },
  { slug: "rodzinne", label: "Rodzinne", description: "Kierunki dobre dla dzieci" },
];

export interface TagLink {
  label: string;
  href: string;
  type: "from" | "to" | "route" | "country" | "theme";
}

export function buildTagCloud(): TagLink[] {
  const tags: TagLink[] = [];
  for (const code of TOP_DEPARTURE_CITIES) {
    const a = findAirport(code);
    if (!a) continue;
    tags.push({
      label: `Tanie loty ${prepFrom(a.city)} ${departureCityLabel(code)}`,
      href: `/loty/${code.toLowerCase()}`,
      type: "from",
    });
  }
  for (const code of TOP_DESTINATIONS) {
    const a = findAirport(code);
    if (!a) continue;
    tags.push({
      label: `Tanie loty do ${cityToGen(a.city)}`,
      href: `/loty/waw/${code.toLowerCase()}`,
      type: "to",
    });
  }
  for (const t of THEMES) {
    tags.push({ label: t.label, href: `/loty/temat/${t.slug}`, type: "theme" });
  }
  for (const country of allCountries()) {
    tags.push({
      label: `Loty do ${countryGen(country)}`,
      href: `/loty/kraj/${countrySlug(country)}`,
      type: "country",
    });
  }
  return tags;
}

// Polish genitive helpers
const GEN_FROM: Record<string, string> = {
  "Warszawa": "Warszawy", "Kraków": "Krakowa", "Gdańsk": "Gdańska", "Katowice": "Katowic",
  "Wrocław": "Wrocławia", "Poznań": "Poznania", "Modlin": "Modlina", "Szczecin": "Szczecina",
  "Rzeszów": "Rzeszowa", "Łódź": "Łodzi", "Bydgoszcz": "Bydgoszczy", "Lublin": "Lublina",
  "Olsztyn": "Olsztyna", "Radom": "Radomia", "Zielona Góra": "Zielonej Góry",
  "Londyn": "Londynu", "Paryż": "Paryża", "Barcelona": "Barcelony", "Madryt": "Madrytu",
  "Rzym": "Rzymu", "Mediolan": "Mediolanu", "Amsterdam": "Amsterdamu", "Frankfurt": "Frankfurtu",
  "Monachium": "Monachium", "Berlin": "Berlina", "Wiedeń": "Wiednia", "Kopenhaga": "Kopenhagi",
  "Sztokholm": "Sztokholmu", "Oslo": "Oslo", "Helsinki": "Helsinek", "Stambuł": "Stambułu",
  "Ateny": "Aten", "Dubaj": "Dubaju", "Doha": "Dohy", "Nowy Jork": "Nowego Jorku",
  "Los Angeles": "Los Angeles", "Miami": "Miami", "Chicago": "Chicago", "Toronto": "Toronto",
  "Bangkok": "Bangkoku", "Phuket": "Phuket", "Bali": "Bali", "Tokio": "Tokio",
  "Singapur": "Singapuru", "Sydney": "Sydney", "São Paulo": "São Paulo", "Meksyk": "Meksyku",
  "Cancún": "Cancún", "Zurych": "Zurychu", "Antalya": "Antalyi", "Heraklion": "Heraklionu",
  "Rodos": "Rodos", "Santorini": "Santorini", "Palma de Mallorca": "Palmy de Mallorca",
  "Malaga": "Malagi", "Alicante": "Alicante", "Teneryfa": "Teneryfy", "Gran Canaria": "Gran Canarii",
  "Faro": "Faro", "Lizbona": "Lizbony", "Porto": "Porto", "Dublin": "Dublina", "Praga": "Pragi",
  "Budapeszt": "Budapesztu", "Split": "Splitu", "Dubrownik": "Dubrownika", "Tirana": "Tirany",
  "Tivat": "Tivatu", "Sofia": "Sofii", "Warna": "Warny", "Burgas": "Burgas",
  "Hurghada": "Hurghady", "Sharm el-Sheikh": "Sharm el-Sheikh", "Marsa Alam": "Marsa Alam",
  "Marrakesz": "Marrakeszu", "Agadir": "Agadiru", "Malta": "Malty", "Larnaka": "Larnaki",
  "Male": "Male", "Punta Cana": "Punta Cana",
  "Glasgow": "Glasgow", "Krabi": "Krabi", "Ostrawa": "Ostrawy", "Pafos": "Pafos",
  "Amman": "Ammanu", "Pekin": "Pekinu", "Billund": "Billund", "Korfu": "Korfu",
  "Fuerteventura": "Fuerteventury", "Baku": "Baku", "Kowno": "Kowna", "Leeds": "Leeds",
  "Lipsk": "Lipska", "Orlando": "Orlando", "Manila": "Manili", "Rijad": "Rijadu",
  "Seul": "Seulu", "Saloniki": "Salonik", "Stavanger": "Stavanger", "Tbilisi": "Tbilisi",
  "Podgorica": "Podgoricy", "Zadar": "Zadaru",
};

const GEN_COUNTRY: Record<string, string> = {
  "Polska": "Polski", "Wielka Brytania": "Wielkiej Brytanii", "Irlandia": "Irlandii",
  "Francja": "Francji", "Hiszpania": "Hiszpanii", "Portugalia": "Portugalii",
  "Włochy": "Włoch", "Niemcy": "Niemiec", "Austria": "Austrii", "Szwajcaria": "Szwajcarii",
  "Holandia": "Holandii", "Belgia": "Belgii", "Luksemburg": "Luksemburga",
  "Dania": "Danii", "Szwecja": "Szwecji", "Norwegia": "Norwegii", "Finlandia": "Finlandii",
  "Islandia": "Islandii", "Czechy": "Czech", "Słowacja": "Słowacji", "Węgry": "Węgier",
  "Rumunia": "Rumunii", "Bułgaria": "Bułgarii", "Chorwacja": "Chorwacji",
  "Słowenia": "Słowenii", "Serbia": "Serbii", "Bośnia i Hercegowina": "Bośni",
  "Czarnogóra": "Czarnogóry", "Albania": "Albanii", "Macedonia Północna": "Macedonii",
  "Łotwa": "Łotwy", "Litwa": "Litwy", "Estonia": "Estonii", "Grecja": "Grecji",
  "Cypr": "Cypru", "Malta": "Malty", "Turcja": "Turcji", "ZEA": "ZEA", "Katar": "Kataru",
  "Arabia Saudyjska": "Arabii Saudyjskiej", "Jordania": "Jordanii", "Liban": "Libanu",
  "Izrael": "Izraela", "Kuwejt": "Kuwejtu", "Bahrajn": "Bahrajnu", "Oman": "Omanu",
  "Egipt": "Egiptu", "Tunezja": "Tunezji", "Maroko": "Maroka", "RPA": "RPA",
  "Kenia": "Kenii", "Tanzania": "Tanzanii", "Etiopia": "Etiopii", "Mauritius": "Mauritiusu",
  "Seszele": "Seszeli", "Azerbejdżan": "Azerbejdżanu", "Armenia": "Armenii",
  "Gruzja": "Gruzji", "Tajlandia": "Tajlandii", "Kambodża": "Kambodży",
  "Wietnam": "Wietnamu", "Malezja": "Malezji", "Singapur": "Singapuru",
  "Indonezja": "Indonezji", "Hongkong": "Hongkongu", "Tajwan": "Tajwanu",
  "Korea Płd.": "Korei Południowej", "Japonia": "Japonii", "Chiny": "Chin",
  "Filipiny": "Filipin", "Malediwy": "Malediw", "Sri Lanka": "Sri Lanki",
  "Indie": "Indii", "Nepal": "Nepalu", "Australia": "Australii", "Nowa Zelandia": "Nowej Zelandii",
  "Fidżi": "Fidżi", "USA": "USA", "Kanada": "Kanady", "Meksyk": "Meksyku",
  "Dominikana": "Dominikany", "Kuba": "Kuby", "Jamajka": "Jamajki", "Bahamy": "Bahamów",
  "Barbados": "Barbadosu", "Brazylia": "Brazylii", "Argentyna": "Argentyny",
  "Chile": "Chile", "Peru": "Peru", "Kolumbia": "Kolumbii", "Ekwador": "Ekwadoru",
  "Urugwaj": "Urugwaju",
};

export function cityFromGen(city: string): string {
  return GEN_FROM[city] ?? city;
}
export function cityToGen(city: string): string {
  return GEN_FROM[city] ?? city;
}
export function countryGen(country: string): string {
  return GEN_COUNTRY[country] ?? country;
}

/**
 * Zwraca poprawny przyimek „z" lub „ze" zależnie od pierwszej litery
 * odmienionej nazwy (np. „ze Szczecina", „ze Sztokholmu", „z Łodzi").
 * Reguła: „ze" przed s/z/ś/ż/ź + spółgłoska oraz przed „Wsz".
 */
export function prepFrom(cityOrCountry: string): "z" | "ze" {
  const declined = GEN_FROM[cityOrCountry] ?? GEN_COUNTRY[cityOrCountry] ?? cityOrCountry;
  return /^(?:[SsZzŚśŻżŹź][a-ząćęłńóśźżA-ZĄĆĘŁŃÓŚŹŻ]|Wsz|wsz)/.test(declined) &&
    !/^(?:[SsZzŚśŻżŹź][aąeęioóuy])/.test(declined) ? "ze" : "z";
}

/**
 * Naprawia polską gramatykę w wygenerowanym tekście (np. z DB SEO):
 *  1. po „z/ze/do" zamienia mianownik na dopełniacz,
 *  2. po deklinacji koryguje „z" → „ze" przed klastrami spółgłoskowymi
 *     (np. „z Szczecina" → „ze Szczecina", „z Sztokholmu" → „ze Sztokholmu").
 */
const ALL_GEN: Record<string, string> = { ...GEN_FROM, ...GEN_COUNTRY };
const PREP_RE = new RegExp(
  `(^|[\\s(\\["'„])(z|ze|do|Z|Ze|Do)\\s+(${Object.keys(ALL_GEN)
    .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .sort((a, b) => b.length - a.length)
    .join("|")})(?=$|[\\s.,;:!?)\\]"'”])`,
  "g",
);
// „z" → „ze" tylko gdy następne słowo zaczyna się klastrem spółgłoskowym
// trudnym do wymówienia po „z" (Sz/St/Sł/Św/Sk/Sm/Sn/Sp/Wsz, podobnie Z/Ś/Ż/Ź).
const ZE_FIX_RE = /(^|[\s(\["'„])([Zz])\s+([SsZzŚśŻżŹź](?:[zZcCkKlLłŁmMnNpPtTwWfFhHbBdDgGrRćĆ])|Wsz|wsz)/g;
export function fixPolishGrammar(text: string | null | undefined): string {
  if (!text) return text ?? "";
  let out = text.replace(PREP_RE, (_m, pre, prep, name) => `${pre}${prep} ${ALL_GEN[name] ?? name}`);
  out = out.replace(ZE_FIX_RE, (_m, pre, z, rest) => `${pre}${z === "Z" ? "Ze" : "ze"} ${rest}`);
  return out;
}

/**
 * Domyślny kierunek (IATA) dla strony „loty z {miasto}". Bazuje na popularności
 * kierunków wyszukiwanych z poszczególnych polskich lotnisk (Londyn dominuje
 * w większości miast — duża diaspora; SZZ/GDN częściej do Oslo).
 * Fallback: LHR (Londyn Heathrow).
 */
const DEFAULT_DEST_BY_CITY: Record<string, string> = {
  WAW: "LHR", // Warszawa → Londyn Heathrow (LOT)
  KRK: "STN", // Kraków → Londyn Stansted (Ryanair)
  GDN: "LTN", // Gdańsk → Londyn Luton (#1 wg DB)
  KTW: "DTM", // Katowice → Dortmund (Wizzair, #1 wg DB; brak STN z KTW)
  WRO: "STN", // Wrocław → Londyn Stansted
  POZ: "STN", // Poznań → Londyn Stansted
  WMI: "STN", // Modlin → Londyn Stansted (Ryanair hub-to-hub)
  SZZ: "STN", // Szczecin → Londyn Stansted (Ryanair, ~45% wyszukiwań)
  RZE: "STN", // Rzeszów → Londyn Stansted (silna polonia w UK)
  LCJ: "STN", // Łódź → Londyn Stansted
  BZG: "STN", // Bydgoszcz → Londyn Stansted
  LUZ: "STN", // Lublin → Londyn Stansted
  RDO: "FCO", // Radom → Rzym Fiumicino (#1 wg DB; brak STN z RDO)
};

export function defaultDestinationFor(fromCode: string): string {
  return DEFAULT_DEST_BY_CITY[fromCode.toUpperCase()] ?? "LHR";
}

/**
 * Najpopularniejsze kierunki per miasto wylotu — używane jako sugestie
 * w pickerze "Dokąd". Kolejność = malejąca popularność wyszukiwań.
 * Po liście per-miasto dokładamy globalny TOP_DESTINATIONS jako fallback.
 */
const POPULAR_DESTS_BY_CITY: Record<string, string[]> = {
  WAR: ["LON", "PAR", "FRA", "AMS", "ROM", "BCN", "BRU", "MIL", "MUC", "MAD", "VIE", "LIS", "PRG", "CPH", "DXA", "OSE", "DUB", "ALC", "AGP", "BUD", "NYC", "HEL", "GVA", "BER", "ISA", "TLV", "PMI", "SZZ", "GDN", "WRO", "KRK"],
  WAW: ["LON", "PAR", "FRA", "AMS", "ROM", "BCN", "BRU", "MIL", "MUC", "MAD", "VIE", "LIS", "PRG", "CPH", "DXA", "OSE", "DUB", "ALC", "AGP", "BUD", "NYC", "HEL", "GVA", "BER", "ISA", "TLV", "PMI", "SZZ", "GDN", "WRO", "KRK"],
  KRK: ["STN", "CDG", "BCN", "FCO", "DUB", "AMS", "MXP", "OSL", "BER", "ALC"],
  GDN: ["STN", "OSL", "BCN", "DUB", "ARN", "CPH", "ALC", "FCO", "AGP", "BER"],
  KTW: ["STN", "DUB", "BCN", "FCO", "AGP", "ALC", "CDG", "AMS", "OSL", "ATH"],
  WRO: ["STN", "DUB", "BCN", "AGP", "ALC", "CDG", "FCO", "AMS", "OSL", "BER"],
  POZ: ["STN", "DUB", "BCN", "AGP", "ALC", "CDG", "OSL", "FCO", "AMS", "BER"],
  WMI: ["STN", "BCN", "AGP", "ALC", "FCO", "DUB", "ATH", "PMI", "TFS", "AYT"],
  SZZ: ["STN", "OSL", "WAW", "DUB", "ALC", "AGP", "BCN", "CPH", "ARN", "BER"],
  RZE: ["STN", "DUB", "EWR", "BCN", "FCO", "OSL", "ATH", "ALC", "AGP", "BER"],
  LCJ: ["STN", "DUB", "AGP", "ALC", "BCN", "ATH", "AYT", "FCO", "BER", "CDG"],
  BZG: ["STN", "DUB", "AGP", "ALC", "BCN", "FCO", "BER", "ATH", "AYT", "OSL"],
  LUZ: ["STN", "DUB", "OSL", "ALC", "BCN", "FCO", "BER", "AGP", "ATH", "CDG"],
  RDO: ["STN", "AGP", "ALC", "BCN", "FCO", "ATH", "AYT", "DUB", "BER", "OSL"],
};

/**
 * Whitelist tras do sitemapy. Zamiast pełnego iloczynu kartezjańskiego
 * TOP_DEPARTURE × TOP_DESTINATIONS (~1700 URL-i), bierzemy tylko top
 * ~10–12 realnie wyszukiwanych kierunków per miasto z POPULAR_DESTS_BY_CITY.
 * Google nie indeksuje pages z "Crawled — currently not indexed" gdy
 * jest ich za dużo; cięcie do ~150 daje znacznie wyższy index rate.
 * Filtrujemy do kodów istniejących w AIRPORTS (żeby nie emitować dead URL).
 */
export const TOP_ROUTE_PAIRS: ReadonlyArray<readonly [string, string]> = (() => {
  const pairs: [string, string][] = [];
  const seen = new Set<string>();
  const valid = new Set(AIRPORTS.map((a) => a.code));
  for (const from of TOP_DEPARTURE_CITIES) {
    if (!valid.has(from)) continue;
    const dests = (POPULAR_DESTS_BY_CITY[from] ?? []).slice(0, 12);
    for (const to of dests) {
      if (!valid.has(to) || to === from) continue;
      const key = `${from}/${to}`;
      if (seen.has(key)) continue;
      seen.add(key);
      pairs.push([from, to]);
    }
  }
  return pairs;
})();

/**
 * Pary zweryfikowane w Aviasales jako nieobsługiwane (brak lotów, także z
 * przesiadką). Nie linkujemy do nich, żeby nie kierować na puste wyniki.
 */
const UNSUPPORTED_PAIRS = new Set<string>(["KTW/BHX", "KTW/BRS"]);
export function isUnsupportedPair(from: string, to: string): boolean {
  const a = airportByLowerCode(from);
  const b = airportByLowerCode(to);
  return !a || !b || a.isCountry || b.isCountry ||
    (a.city === b.city && a.country === b.country) ||
    UNSUPPORTED_PAIRS.has(`${from.toUpperCase()}/${to.toUpperCase()}`);
}

export function popularDestinationsFor(
  fromCode: string,
  dbCodes: string[] = [],
  currentToCode?: string,
  searchedCodes: string[] = [],
): string[] {
  const upper = fromCode.toUpperCase();
  const perCity = POPULAR_DESTS_BY_CITY[upper] ?? [];
  // Rodzeństwo aktualnie wybranego celu — inne lotniska tego samego miasta
  // (np. wybrany CDG → dorzucamy ORY + BVA). Sam wybrany kod pomijamy, żeby
  // jednoportowe miasto (np. BCN) nie wskakiwało na #1 zasłaniając realny ranking.
  const toUpper = currentToCode?.toUpperCase();
  const ref = toUpper ? AIRPORTS.find((x) => x.code === toUpper) : undefined;
  // Rodzeństwo dorzucamy tylko jeśli realnie istnieją trasy z tego origin
  // do tych lotnisk (czyli pojawiają się w dbCodes). Inaczej np. ze Szczecina
  // do STN nie ma sensu sugerować LHR/LGW/LTN/LCY — bo tam loty nie latają.
  const dbUpper = new Set(dbCodes.map((x) => x.toUpperCase()));
  const siblingsOfTo = ref
    ? AIRPORTS
        .filter((a) => a.city === ref.city && a.country === ref.country && a.code !== toUpper && dbUpper.has(a.code))
        .map((a) => a.code)
    : [];
  // Generalizacja tej samej zasady na całą listę: jeśli z danego origin DB
  // potwierdza loty do konkretnych lotnisk w mieście wieloportowym (np. SZZ → STN),
  // to z hardcoded per-city i globalnego topu odrzucamy pozostałe lotniska tego
  // samego miasta. Zapobiega to wracaniu LHR/LGW/LTN przez TOP_DESTINATIONS.
  const servedByCity = new Map<string, Set<string>>(); // "Londyn|Wielka Brytania" → {STN}
  for (const code of dbUpper) {
    const a = AIRPORTS.find((x) => x.code === code);
    if (!a) continue;
    const key = `${a.city}|${a.country}`;
    if (!servedByCity.has(key)) servedByCity.set(key, new Set());
    servedByCity.get(key)!.add(code);
  }
  const isBlockedSibling = (code: string) => {
    const a = AIRPORTS.find((x) => x.code === code);
    if (!a) return false;
    const key = `${a.city}|${a.country}`;
    const served = servedByCity.get(key);
    return !!served && !served.has(code);
  };
  // Kolejność: 0) rodzeństwo celu (multi-airport grouping, ograniczone do dbCodes),
  // 1) realne wyszukiwania użytkowników z `search_queries` (sygnał popytu —
  //    np. WAW jest często szukane ze SZZ, więc trafia wysoko nawet jeśli
  //    AI-ranking w `city_top_destinations` jeszcze go nie ma),
  // 2) DB ranking (świeży top z AI/Firecrawl), 3) hardcoded per-city,
  // 4) globalny top.
  const seen = new Set<string>([upper]);
  const out: string[] = [];
  const merged = [
    ...siblingsOfTo,
    ...searchedCodes.map((x) => x.toUpperCase()),
    ...dbCodes.map((x) => x.toUpperCase()),
    ...perCity,
    ...TOP_DESTINATIONS,
  ];
  for (const c of merged) {
    if (seen.has(c)) continue;
    seen.add(c);
    if (isBlockedSibling(c)) continue;
    if (isUnsupportedPair(upper, c)) continue;
    out.push(c);
  }
  // Auto-grupowanie metro: jeśli dla danego miasta wieloportowego DB potwierdza
  // ≥2 lotniska (np. WAW→LHR i WAW→STN), zamieniamy pierwsze wystąpienie
  // dowolnego członka w `out` na kod metro (LON), a kolejnych członków pomijamy.
  // Dzięki temu w sugestiach pojawia się grupa „(wszystkie lotniska)" zamiast
  // pojedynczych portów — zgodnie z realną siatką połączeń z origin.
  const metros = AIRPORTS.filter((a) => a.isMetro && a.members && a.members.length > 0);
  const memberToMetro = new Map<string, string>();
  // 1) Jawne metra obecne już w `out` (np. LON wstawiony ręcznie w POPULAR_DESTS)
  //    — chowamy wszystkie ich konkretne lotniska poniżej.
  const outSet = new Set(out);
  for (const m of metros) {
    if (outSet.has(m.code)) {
      for (const code of m.members ?? []) memberToMetro.set(code, m.code);
    }
  }
  // 2) Auto-grupowanie: jeśli DB potwierdza ≥2 lotniska tego miasta, zamieniamy
  //    pierwsze wystąpienie członka na kod metro, kolejne pomijamy.
  for (const m of metros) {
    if (outSet.has(m.code)) continue;
    const served = (m.members ?? []).filter((code) => dbUpper.has(code));
    if (served.length >= 2) {
      for (const code of m.members ?? []) memberToMetro.set(code, m.code);
    }
  }
  // 3) Fallback (brak DB, np. origin = „cały kraj" XPL): grupujemy też wtedy,
  //    gdy w samej liście `out` znajdują się ≥2 lotniska tego miasta —
  //    np. TOP_DESTINATIONS zawiera LHR+LGW+STN+LTN i CDG+ORY, które bez
  //    tego pojawiały się jako 4× Londyn + 2× Paryż w pickerze.
  for (const m of metros) {
    if (outSet.has(m.code)) continue;
    const present = (m.members ?? []).filter((code) => outSet.has(code));
    if (present.length >= 2) {
      for (const code of m.members ?? []) memberToMetro.set(code, m.code);
    }
  }
  if (memberToMetro.size === 0) return out;
  const grouped: string[] = [];
  const placedMetros = new Set<string>();
  for (const c of out) {
    if (metros.some((m) => m.code === c)) {
      // metro już obecne — przepuść raz
      if (placedMetros.has(c)) continue;
      placedMetros.add(c);
      grouped.push(c);
      continue;
    }
    const metro = memberToMetro.get(c);
    if (!metro) {
      grouped.push(c);
      continue;
    }
    if (placedMetros.has(metro)) continue;
    placedMetros.add(metro);
    grouped.push(metro);
  }
  return grouped;
}

/**
 * Sugestie pola SKĄD: priorytet dla lotnisk tego samego miasta co aktualnie
 * wybrane (np. WAW + WMI), potem geograficznie najbliższe duże porty
 * (mieszanka PL + sąsiednie kraje), potem reszta polskich, potem huby EU.
 *
 * Mapa NEAREST_AIRPORTS_BY_CITY zawiera ręcznie zweryfikowane sąsiedztwa
 * dla 13 miast wylotu z szybkich linków na stronie głównej. Kolejność =
 * malejąca bliskość/atrakcyjność (duże lotniska > regionalne).
 */
const NEAREST_AIRPORTS_BY_CITY: Record<string, string[]> = {
  // Mazowsze: drugie lotnisko + bliskie regionalne + sąsiednia stolica regionu
  WAW: ["WMI", "LCJ", "RDO", "KTW", "POZ", "GDN", "KRK", "SZZ", "RZE"],
  WMI: ["WAW", "LCJ", "RDO", "KTW", "POZ", "GDN", "KRK", "SZZ", "RZE"],
  // Małopolska/Śląsk
  KRK: ["KTW", "RZE", "WRO", "BTS", "VIE", "PRG", "WAW"],
  KTW: ["KRK", "WRO", "OSR" /* nie ma — odpadnie */, "PRG", "VIE", "BTS", "POZ", "WAW"],
  // Pomorze/Wielkopolska/Zachodniopomorskie
  GDN: ["BZG", "SZZ", "POZ", "WMI", "WAW", "RIX", "CPH"],
  POZ: ["WRO", "SZZ", "BZG", "BER", "WAW", "GDN", "KTW"],
  SZZ: ["POZ", "BER", "GDN", "BZG", "WRO", "CPH"],
  BZG: ["POZ", "GDN", "WMI", "WAW", "SZZ", "WRO", "LCJ"],
  // Dolny Śląsk
  WRO: ["POZ", "KTW", "KRK", "PRG", "BER", "WAW", "BZG"],
  // Łódzkie / centrum
  LCJ: ["WAW", "WMI", "POZ", "WRO", "KTW", "BZG", "RDO"],
  // Lubelszczyzna / Podkarpacie
  LUZ: ["WAW", "WMI", "RZE", "KRK", "KTW", "RDO"],
  RZE: ["KRK", "KTW", "LUZ", "WAW", "BUD", "BTS"],
  RDO: ["WAW", "WMI", "LCJ", "KRK", "KTW", "LUZ"],
};

const TOP_EU_HUBS = ["LHR", "CDG", "BER", "FRA", "AMS", "MUC", "FCO", "BCN", "MAD"];

export function popularOriginsFor(fromCode: string): string[] {
  const upper = fromCode.toUpperCase();
  const ref = AIRPORTS.find((x) => x.code === upper);
  const sameCity = ref
    ? AIRPORTS.filter((a) => a.city === ref.city && a.country === ref.country).map((a) => a.code)
    : [];
  const nearest = NEAREST_AIRPORTS_BY_CITY[upper] ?? [];
  // Fallback: pozostałe polskie porty (gdy miasto spoza listy quick-links).
  const polishRest = AIRPORTS.filter((a) => a.country === "Polska").map((a) => a.code);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of [...sameCity, ...nearest, ...polishRest, ...TOP_EU_HUBS]) {
    if (seen.has(c)) continue;
    // Pomiń kody, których nie ma w bazie lotnisk (np. literówki w mapie).
    if (!AIRPORTS.some((a) => a.code === c)) continue;
    seen.add(c);
    out.push(c);
  }
  return out;
}

export function airportByLowerCode(code: string) {
  const upper = code.toUpperCase();
  return AIRPORTS.find((a) => a.code === upper);
}
